import type { MapMouseEvent, MapRef } from 'react-map-gl/mapbox'
import type { TourRouteCoordinate } from '@/features/tour/tour-route-map-utils'
import type { IAreaBoundary } from '@/shared/api/types/destination-types'
import type { IPOI } from '@/shared/api/types/poi-types'
import type { ITourPoi } from '@/shared/api/types/tour-types'
import { MousePointerClick, Pencil, Route, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Map, { Layer, Marker, NavigationControl, Source } from 'react-map-gl/mapbox'
import {
  appendCoordinate,
  createBoundaryGeoJson,
  getBoundaryBounds,
  getPoiCoordinatesFromSelection,
  getRouteBounds,
  insertCoordinateAt,
  mergeBounds,
  midpoint,
  normalizeTourCoordinates,
  poiRangesToFeatureCollection,
  removeCoordinateAt,
  toRouteFeatureCollection,
  updateCoordinateAt,
  validateDestinationCenter,
} from '@/features/tour/tour-route-map-utils'
import { TENANT_THEME_DEFAULT_COLORS, UI_THEME_COLORS } from '@/shared/constants/theme-colors'
import { cn } from '@/shared/libs/cn'
import { Button } from '@/shared/ui/button'
import 'mapbox-gl/dist/mapbox-gl.css'

interface TourMapPreviewProps {
  selectedPois: ITourPoi[]
  poiMap: Record<string, IPOI>
  coordinates: number[][]
  destinationAreaBoundary?: IAreaBoundary | null
  destinationCenterLat?: number | null
  destinationCenterLng?: number | null
  editable?: boolean
  onCoordinatesChange?: (next: TourRouteCoordinate[]) => void
}

const DEFAULT_CENTER = { latitude: 16.047079, longitude: 108.20623, zoom: 16 }

const FIT_BOUNDS_OPTIONS = { padding: 60, duration: 0, maxZoom: 16 } as const
const DESTINATION_CENTER_ZOOM = 14
const FIRST_TOUR_POINT_ZOOM = 16

const ROUTE_LINE_COLOR = '#6366F1'
const VERTEX_COLOR = '#6366F1'
const MIDPOINT_COLOR = '#A5B4FC'

export function TourMapPreview({
  selectedPois,
  poiMap,
  coordinates,
  destinationAreaBoundary = null,
  destinationCenterLat = null,
  destinationCenterLng = null,
  editable = false,
  onCoordinatesChange,
}: TourMapPreviewProps) {
  const { t } = useTranslation()
  const mapboxToken = import.meta.env.VITE_MAPBOX_TOKEN
  const mapStyle = import.meta.env.VITE_MAPBOX_STYLE || 'mapbox://styles/mapbox/streets-v12'
  const mapRef = useRef<MapRef>(null)
  const [viewState, setViewState] = useState({
    latitude: DEFAULT_CENTER.latitude,
    longitude: DEFAULT_CENTER.longitude,
    zoom: DEFAULT_CENTER.zoom,
  })
  const [isDrawing, setIsDrawing] = useState(false)
  const isDraggingRef = useRef(false)

  const routeCoordinates = useMemo(() => normalizeTourCoordinates(coordinates), [coordinates])
  const routeGeoJson = useMemo(() => toRouteFeatureCollection(routeCoordinates), [routeCoordinates])
  const poiCoordinates = useMemo(
    () => getPoiCoordinatesFromSelection(selectedPois, poiMap),
    [selectedPois, poiMap],
  )
  const poiRangesGeoJson = useMemo(
    () => poiRangesToFeatureCollection(selectedPois, poiMap),
    [selectedPois, poiMap],
  )
  const destinationBoundaryGeoJson = useMemo(
    () => createBoundaryGeoJson(destinationAreaBoundary),
    [destinationAreaBoundary],
  )
  const destinationBoundaryBounds = useMemo(
    () => getBoundaryBounds(destinationAreaBoundary),
    [destinationAreaBoundary],
  )
  const hasDestinationCenter = validateDestinationCenter([
    destinationCenterLat,
    destinationCenterLng,
  ])
  const firstTourPoint = poiCoordinates[0] ?? routeCoordinates[0] ?? null
  const firstTourPointLat = firstTourPoint?.[0] ?? null
  const firstTourPointLng = firstTourPoint?.[1] ?? null

  useEffect(() => {
    if (isDrawing) return

    const bounds = mergeBounds([
      getRouteBounds(routeCoordinates, poiCoordinates),
      destinationBoundaryBounds,
    ])
    if (!bounds && !hasDestinationCenter && firstTourPoint === null) {
      return
    }

    const map = mapRef.current?.getMap()
    if (!map) {
      return
    }

    const applyCamera = () => {
      if (firstTourPointLat !== null && firstTourPointLng !== null) {
        setViewState({
          latitude: firstTourPointLat,
          longitude: firstTourPointLng,
          zoom: FIRST_TOUR_POINT_ZOOM,
        })
        map.jumpTo({
          center: [firstTourPointLng, firstTourPointLat],
          zoom: FIRST_TOUR_POINT_ZOOM,
        })
        return
      }

      if (hasDestinationCenter && destinationCenterLat !== null && destinationCenterLng !== null) {
        const nextZoom = Math.max(map.getZoom(), DESTINATION_CENTER_ZOOM)
        setViewState({
          latitude: destinationCenterLat,
          longitude: destinationCenterLng,
          zoom: nextZoom,
        })
        map.jumpTo({
          center: [destinationCenterLng, destinationCenterLat],
          zoom: nextZoom,
        })
        return
      }

      if (bounds) {
        map.fitBounds(bounds, FIT_BOUNDS_OPTIONS)
      }
    }

    if (map.loaded()) {
      applyCamera()
    } else {
      map.once('load', applyCamera)
    }

    return () => {
      map.off('load', applyCamera)
    }
  }, [
    isDrawing,
    routeCoordinates,
    poiCoordinates,
    destinationBoundaryBounds,
    destinationCenterLat,
    destinationCenterLng,
    hasDestinationCenter,
    firstTourPoint,
    firstTourPointLat,
    firstTourPointLng,
  ])

  function handleMapClick(event: MapMouseEvent) {
    if (!isDrawing || !onCoordinatesChange) return
    if (isDraggingRef.current) {
      isDraggingRef.current = false
      return
    }
    const { lat, lng } = event.lngLat
    onCoordinatesChange(appendCoordinate(routeCoordinates, [lat, lng]))
  }

  function handleVertexDragEnd(index: number, lat: number, lng: number) {
    if (!onCoordinatesChange) return
    isDraggingRef.current = true
    onCoordinatesChange(updateCoordinateAt(routeCoordinates, index, [lat, lng]))
  }

  function handleVertexDelete(index: number) {
    if (!onCoordinatesChange) return
    onCoordinatesChange(removeCoordinateAt(routeCoordinates, index))
  }

  function handleMidpointClick(index: number, point: TourRouteCoordinate) {
    if (!onCoordinatesChange) return
    onCoordinatesChange(insertCoordinateAt(routeCoordinates, index, point))
  }

  function handleGenerateFromPois() {
    if (!onCoordinatesChange) return
    const fromPois = getPoiCoordinatesFromSelection(selectedPois, poiMap)
    onCoordinatesChange(fromPois)
  }

  function handleClearRoute() {
    if (!onCoordinatesChange) return
    onCoordinatesChange([])
    setIsDrawing(false)
  }

  function renderFallback() {
    return (
      <div
        style={{
          height: 360,
          display: 'grid',
          placeItems: 'center',
          background: UI_THEME_COLORS.poiMapGradient,
        }}
      >
        <p className="text-sm text-zinc-500">{t('poi.map.missingTokenDescription')}</p>
      </div>
    )
  }

  function renderToolbar() {
    if (!editable) return null
    return (
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-zinc-100 bg-zinc-50">
        <Button
          type="button"
          variant={isDrawing ? 'default' : 'outline'}
          size="sm"
          onClick={() => setIsDrawing(!isDrawing)}
        >
          {isDrawing ? (
            <>
              <Pencil className="mr-1.5 h-3.5 w-3.5" />
              {t('tour.finishDrawing')}
            </>
          ) : (
            <>
              <MousePointerClick className="mr-1.5 h-3.5 w-3.5" />
              {t('tour.drawRoute')}
            </>
          )}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleGenerateFromPois}
          disabled={selectedPois.length < 2}
        >
          <Route className="mr-1.5 h-3.5 w-3.5" />
          {t('tour.generateRouteFromPois')}
        </Button>
        {routeCoordinates.length > 0 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleClearRoute}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="mr-1.5 h-3.5 w-3.5" />
            {t('tour.clearRoute')}
          </Button>
        )}
        <span className="ml-auto text-xs text-zinc-500">
          {t('tour.routePoints')}:{routeCoordinates.length}
        </span>
      </div>
    )
  }

  function renderVertexMarkers() {
    if (!editable || !isDrawing) return null
    return routeCoordinates.map((coord, index) => (
      <Marker
        key={`vertex-${index}`}
        longitude={coord[1]}
        latitude={coord[0]}
        draggable
        anchor="center"
        onDragEnd={(event) => {
          handleVertexDragEnd(index, event.lngLat.lat, event.lngLat.lng)
        }}
      >
        <div className="group relative flex items-center justify-center">
          <div
            className="h-6 w-6 rounded-full border-2 border-white text-[10px] font-bold shadow-md flex items-center justify-center text-white"
            style={{ backgroundColor: VERTEX_COLOR }}
          >
            {index + 1}
          </div>
          <button
            type="button"
            className="absolute -top-2.5 -right-2.5 hidden group-hover:flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-white text-[8px] font-bold shadow"
            onClick={(e) => {
              e.stopPropagation()
              handleVertexDelete(index)
            }}
            title={t('tour.deletePoint')}
          >
            ✕
          </button>
        </div>
      </Marker>
    ))
  }

  function renderMidpointMarkers() {
    if (!editable || !isDrawing || routeCoordinates.length < 2) return null
    const markers = []
    for (let i = 0; i < routeCoordinates.length - 1; i++) {
      const a = routeCoordinates[i]
      const b = routeCoordinates[i + 1]
      if (!a || !b) {
        continue
      }
      const mid = midpoint(a, b)
      markers.push(
        <Marker key={`mid-${i}`} longitude={mid[1]} latitude={mid[0]} anchor="center">
          <button
            type="button"
            className="flex h-4 w-4 items-center justify-center rounded-full border border-white text-[9px] font-bold text-white shadow opacity-60 hover:opacity-100 transition-opacity"
            style={{ backgroundColor: MIDPOINT_COLOR }}
            onClick={(e) => {
              e.stopPropagation()
              handleMidpointClick(i + 1, mid)
            }}
            title={t('tour.addPoint')}
          >
            +
          </button>
        </Marker>,
      )
    }
    return markers
  }

  return (
    <div className="border border-zinc-200 rounded-xl overflow-hidden">
      {renderToolbar()}
      {editable && isDrawing && (
        <div className="px-3 py-1.5 bg-indigo-50 border-b border-indigo-100">
          <p className="text-xs text-indigo-700">{t('tour.routeDrawHint')}</p>
        </div>
      )}
      {!mapboxToken ? (
        renderFallback()
      ) : (
        <div className="relative h-[360px] lg:h-[460px]">
          <Map
            ref={mapRef}
            mapboxAccessToken={mapboxToken}
            mapStyle={mapStyle}
            longitude={viewState.longitude}
            latitude={viewState.latitude}
            zoom={viewState.zoom}
            onMove={(event) =>
              setViewState({
                latitude: event.viewState.latitude,
                longitude: event.viewState.longitude,
                zoom: event.viewState.zoom,
              })
            }
            onClick={handleMapClick}
            {...(isDrawing ? { cursor: 'crosshair' } : {})}
            onLoad={(event) => {
              if (firstTourPointLat !== null && firstTourPointLng !== null) {
                setViewState({
                  latitude: firstTourPointLat,
                  longitude: firstTourPointLng,
                  zoom: FIRST_TOUR_POINT_ZOOM,
                })
                event.target.jumpTo({
                  center: [firstTourPointLng, firstTourPointLat],
                  zoom: FIRST_TOUR_POINT_ZOOM,
                })
                return
              }

              if (
                hasDestinationCenter &&
                destinationCenterLat !== null &&
                destinationCenterLng !== null
              ) {
                setViewState({
                  latitude: destinationCenterLat,
                  longitude: destinationCenterLng,
                  zoom: DESTINATION_CENTER_ZOOM,
                })
                event.target.jumpTo({
                  center: [destinationCenterLng, destinationCenterLat],
                  zoom: DESTINATION_CENTER_ZOOM,
                })
                return
              }

              const bounds = mergeBounds([
                getRouteBounds(routeCoordinates, poiCoordinates),
                destinationBoundaryBounds,
              ])
              if (bounds) {
                event.target.fitBounds(bounds, FIT_BOUNDS_OPTIONS)
              }
            }}
            reuseMaps
            style={{ width: '100%', height: '100%' }}
          >
            <NavigationControl position="top-right" />
            {destinationBoundaryGeoJson && (
              <Source
                id="tour-destination-boundary"
                type="geojson"
                data={destinationBoundaryGeoJson}
              >
                <Layer
                  id="tour-destination-boundary-fill"
                  type="fill"
                  paint={{ 'fill-color': '#22C55E', 'fill-opacity': 0.12 }}
                />
                <Layer
                  id="tour-destination-boundary-outline"
                  type="line"
                  layout={{ 'line-join': 'round', 'line-cap': 'round' }}
                  paint={{ 'line-color': '#16A34A', 'line-width': 2, 'line-dasharray': [2, 1] }}
                />
              </Source>
            )}
            {poiRangesGeoJson && (
              <Source id="tour-poi-ranges" type="geojson" data={poiRangesGeoJson}>
                <Layer
                  id="tour-poi-range-fill"
                  type="fill"
                  paint={{
                    'fill-color': TENANT_THEME_DEFAULT_COLORS.primary,
                    'fill-opacity': 0.1,
                  }}
                />
                <Layer
                  id="tour-poi-range-outline"
                  type="line"
                  layout={{ 'line-join': 'round', 'line-cap': 'round' }}
                  paint={{
                    'line-color': TENANT_THEME_DEFAULT_COLORS.primary,
                    'line-width': 1.5,
                    'line-opacity': 0.4,
                  }}
                />
              </Source>
            )}

            {routeGeoJson && (
              <Source id="tour-route-line" type="geojson" data={routeGeoJson}>
                <Layer
                  id="tour-route-line-layer"
                  type="line"
                  layout={{ 'line-join': 'round', 'line-cap': 'round' }}
                  paint={{
                    'line-color': ROUTE_LINE_COLOR,
                    'line-width': 3,
                    'line-opacity': 0.85,
                  }}
                />
              </Source>
            )}

            {renderMidpointMarkers()}
            {renderVertexMarkers()}

            {selectedPois
              .slice()
              .sort((a, b) => a.order - b.order)
              .map((item, index) => {
                const poi = poiMap[item.poiId]
                if (!poi) {
                  return null
                }

                return (
                  <Marker
                    key={item.poiId}
                    longitude={poi.longitude}
                    latitude={poi.latitude}
                    anchor="bottom"
                  >
                    <div className="group relative flex flex-col items-center">
                      <div
                        className={cn(
                          'h-7 w-7 rounded-full border-2 text-xs font-semibold shadow',
                          'bg-emerald-500 text-white border-white',
                        )}
                        title={poi.localizedData[0]?.name ?? item.poiId}
                      >
                        <span className="grid h-full place-items-center">{index + 1}</span>
                      </div>
                    </div>
                  </Marker>
                )
              })}
          </Map>
        </div>
      )}
    </div>
  )
}
